import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BACKEND_URL } from "./../backendconfig";
import "./AdminLogin.css";

const api = `${BACKEND_URL.replace(/\/+$/, "")}/admin-auth.php`;

async function sendRequest(action, values) {
  const response = await fetch(`${api}?action=${action}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(values),
  });

  let data;

  try {
    data = await response.json();
  } catch {
    throw new Error("Invalid API response. Check admin-auth.php.");
  }

  if (!response.ok || !data.ok) {
    throw new Error(data.message || "Unable to process request.");
  }

  return data;
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const forgotDialog = useRef(null);
  const submitting = useRef(false);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [recoveryError, setRecoveryError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem("ltc-admin-remember") || "null",
      );

      if (
        saved &&
        typeof saved.email === "string" &&
        typeof saved.password === "string"
      ) {
        setUsername(saved.email);
        setPassword(saved.password);
        setRememberMe(true);
      }
    } catch {
      // Continue if browser storage is unavailable or invalid.
    }
  }, []);

  function handleRememberChange(event) {
    const checked = event.target.checked;

    setRememberMe(checked);

    if (!checked) {
      try {
        localStorage.removeItem("ltc-admin-remember");
      } catch {
        // Browser storage may be unavailable.
      }
    }
  }
  async function handleLogin(event) {
    event.preventDefault();

    if (submitting.current) return;

    submitting.current = true;
    setBusy(true);
    setError("");
    setSuccess("");

    try {
      const data = await sendRequest("login", {
        username: username.trim(),
        password,
      });

      if (!data.authenticated) {
        throw new Error("Unable to log in.");
      }
      if (!data.authenticated) {
        throw new Error("Unable to log in.");
      }

      try {
        if (rememberMe) {
          localStorage.setItem(
            "ltc-admin-remember",
            JSON.stringify({
              email: username.trim(),
              password,
            }),
          );
        } else {
          localStorage.removeItem("ltc-admin-remember");
        }
      } catch {
        // Login still succeeds if browser storage is unavailable.
      }

      navigate("/MyAdmin/courses", { replace: true });
      navigate("/MyAdmin/courses", { replace: true });
    } catch (error) {
      setError(error.message);
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  function openRecovery() {
    setStep("email");
    setEmail("");
    setResetToken("");
    setNewPassword("");
    setConfirmPassword("");
    setRecoveryError("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    forgotDialog.current?.showModal();
  }

  function clearRecovery() {
    setResetToken("");
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  }
  async function handleRecovery(event) {
    event.preventDefault();

    if (submitting.current) return;

    if (step === "password" && newPassword !== confirmPassword) {
      setRecoveryError("Passwords do not match.");
      return;
    }

    submitting.current = true;
    setBusy(true);
    setRecoveryError("");

    try {
      if (step === "email") {
        const data = await sendRequest("check-email", {
          email: email.trim(),
        });

        setResetToken(data.resetToken);
        setStep("password");
      } else {
        const data = await sendRequest("reset-password", {
          resetToken,
          password: newPassword,
          confirmPassword,
        });

        forgotDialog.current?.close();
        clearRecovery();

        setUsername(email.trim());
        setPassword("");
        setError("");
        setSuccess(data.message);
      }
    } catch (error) {
      setRecoveryError(error.message);
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  return (
    <main className="al-page">
      <section className="al-card">
        <img
          src="/logo.jpg"
          alt="LifeTime"
          className="al-logo"
          width="74"
          height="74"
        />

        <span className="al-eyebrow">LIFETIME ADMIN</span>

        <h1>Welcome back</h1>
        <p>Sign in to manage your website.</p>

        {error && (
          <div className="al-error" role="alert">
            {error}
          </div>
        )}

        {success && (
          <div className="al-success" role="status">
            {success}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <fieldset disabled={busy}>
            <label htmlFor="al-username">Email</label>

            <input
              id="al-username"
              type="text"
              required
              autoComplete="username"
              maxLength={254}
              placeholder="Enter email"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />

            <label htmlFor="al-password">Password</label>

            <div className="al-password">
              <input
                id="al-password"
                type={showPassword ? "text" : "password"}
                required
                autoComplete="current-password"
                placeholder="Enter password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />

              <button
                type="button"
                className="al-eye"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((previous) => !previous)}
              >
                <i
                  className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}
                  aria-hidden="true"
                />
              </button>
            </div>

            <div className="al-login-options">
              <label className="al-remember" htmlFor="al-remember">
                <input
                  id="al-remember"
                  type="checkbox"
                  name="remember"
                  checked={rememberMe}
                  onChange={handleRememberChange}
                />

                <span>Remember me</span>
              </label>

              <button
                type="button"
                className="al-forgot"
                onClick={openRecovery}
              >
                Forgot Password?
              </button>
            </div>

            <button type="submit" className="al-submit">
              {busy ? "Please wait…" : "Sign In"}
              <i className="bi bi-arrow-right" aria-hidden="true" />
            </button>
          </fieldset>
        </form>

        <dialog
          ref={forgotDialog}
          className="al-forgot-dialog"
          aria-labelledby="al-forgot-title"
          onClose={clearRecovery}
          onCancel={(event) => {
            if (busy) event.preventDefault();
          }}
        >
          <div className="al-forgot-header">
            <h2 id="al-forgot-title">
              {step === "email" ? "Forgot Password?" : "Change Password"}
            </h2>

            <button
              type="button"
              className="al-forgot-close"
              aria-label="Close password recovery"
              disabled={busy}
              onClick={() => forgotDialog.current?.close()}
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>

          <p className="al-recovery-description">
            {step === "email"
              ? "Enter your registered admin email address."
              : `Set a new password for ${email}.`}
          </p>

          {recoveryError && (
            <div className="al-error" role="alert">
              {recoveryError}
            </div>
          )}

          <form onSubmit={handleRecovery}>
            <fieldset disabled={busy}>
              {step === "email" ? (
                <>
                  <label htmlFor="al-reset-email">Email Address</label>

                  <input
                    id="al-reset-email"
                    type="email"
                    required
                    autoComplete="email"
                    maxLength={254}
                    placeholder="Enter registered email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </>
              ) : (
                <>
                  <label htmlFor="al-new-password">New Password</label>

                  <div className="al-password">
                    <input
                      id="al-new-password"
                      type={showNewPassword ? "text" : "password"}
                      required
                      maxLength={255}
                      autoComplete="new-password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                    />

                    <button
                      type="button"
                      className="al-eye"
                      aria-label={
                        showNewPassword
                          ? "Hide new password"
                          : "Show new password"
                      }
                      aria-pressed={showNewPassword}
                      aria-controls="al-new-password"
                      onClick={() =>
                        setShowNewPassword((previous) => !previous)
                      }
                    >
                      <i
                        className={`bi ${showNewPassword ? "bi-eye-slash" : "bi-eye"}`}
                        aria-hidden="true"
                      />
                    </button>
                  </div>

                  <label htmlFor="al-confirm-password">Confirm Password</label>

                  <div className="al-password">
                    <input
                      id="al-confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      maxLength={255}
                      autoComplete="new-password"
                      placeholder="Enter new password again"
                      value={confirmPassword}
                      onChange={(event) =>
                        setConfirmPassword(event.target.value)
                      }
                    />

                    <button
                      type="button"
                      className="al-eye"
                      aria-label={
                        showConfirmPassword
                          ? "Hide confirm password"
                          : "Show confirm password"
                      }
                      aria-pressed={showConfirmPassword}
                      aria-controls="al-confirm-password"
                      onClick={() =>
                        setShowConfirmPassword((previous) => !previous)
                      }
                    >
                      <i
                        className={`bi ${showConfirmPassword ? "bi-eye-slash" : "bi-eye"}`}
                        aria-hidden="true"
                      />
                    </button>
                  </div>

                  <button
                    type="button"
                    className="al-forgot"
                    onClick={() => {
                      setStep("email");
                      clearRecovery();
                      setRecoveryError("");
                    }}
                  >
                    Change Email
                  </button>
                </>
              )}

              <button type="submit" className="al-submit">
                {busy
                  ? "Please wait…"
                  : step === "email"
                    ? "Verify Email"
                    : "Update Password"}
              </button>
            </fieldset>
          </form>
        </dialog>
      </section>
    </main>
  );
}
